import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Sparkles } from 'lucide-react';

const PHASES = [
  'Mapping NCERT chapter to blueprint…',
  'Selecting PYQs + module questions…',
  'Composing diagram-based items (SVG)…',
  'Balancing difficulty mix…',
  'Packaging your set…',
];

const TIPS = [
  'Tip: Attempt easy questions first, mark tough ones for review.',
  'Tip: Diagrams often carry a hidden clue — read them before options.',
  'Tip: Watch units in numericals — they eliminate 1 or 2 options instantly.',
  'Tip: In NEET Biology, exact NCERT wording matters. Recall precise lines.',
  'Tip: JEE questions often combine two concepts — read the stem twice.',
];

export function AILoader({ chapter, subject, setNumber, track }) {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [tipIdx, setTipIdx] = useState(0);
  const [progress, setProgress] = useState(6);

  useEffect(() => {
    const p = setInterval(() => {
      setPhaseIdx((i) => (i + 1) % PHASES.length);
    }, 5500);
    const t = setInterval(() => {
      setTipIdx((i) => (i + 1) % TIPS.length);
    }, 6000);
    const pr = setInterval(() => {
      setProgress((v) => (v < 92 ? v + Math.max(1, Math.round((95 - v) / 15)) : v));
    }, 900);
    return () => {
      clearInterval(p);
      clearInterval(t);
      clearInterval(pr);
    };
  }, []);

  return (
    <div
      className="mx-auto flex min-h-[60vh] max-w-2xl items-center justify-center"
      data-testid="ai-generation-loading"
    >
      <Card className="w-full">
        <CardContent className="space-y-5 p-6 sm:p-8">
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-[hsl(var(--accent))] text-accent-foreground">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Preparing your test</div>
              <div className="font-display text-lg font-semibold">
                {track} · {subject} · Set {setNumber}
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-secondary/50 p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Chapter</div>
            <div className="text-sm font-medium">{chapter}</div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{PHASES[phaseIdx]}</span>
              <span className="font-mono-ui text-xs text-muted-foreground">{progress}%</span>
            </div>
            <Progress value={progress} data-testid="ai-generation-progress" />
            <div className="text-[11px] text-muted-foreground">
              This usually loads instantly (cached sets). First-ever generation for a chapter takes 30–60 seconds. Please don’t refresh.
            </div>
          </div>

          <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
            {TIPS[tipIdx]}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
